<template>
  <v-container fluid>
    <v-row>
      <global-EventFilterMenu
        header="Event Filter"
        showStallholders
        headerButtonText="Takings Report"
        :headerButtonAction="generateTakingsReport"
        :headerButtonLoading="takingsReportLoading"
        secondHeaderButtonText="Voucher Report"
        :secondHeaderButtonAction="generateVoucherReport"
      />
    </v-row>

    <v-row v-if="takingsTotalSalesLoading && takingsJobProgress">
      <v-col cols="12">
        <v-alert dense colored-border border="left" color="info" class="mb-0">
          <div class="d-flex align-center">
            <v-progress-circular
              indeterminate
              size="18"
              width="2"
              class="mr-3"
            />
            <span>{{ takingsJobProgress }}</span>
          </div>
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1">
            <v-icon left dense> mdi-chart-pie </v-icon>
            Total Sales
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col lg="6" md="12" sm="6" cols="12">
                <skeletons-summary :loading="takingsTotalSalesLoading">
                  <v-card class="primary mb-2" dark>
                    <v-card-title class="py-1 title">
                      <v-icon left> mdi-cash </v-icon>
                      Total Sales:
                    </v-card-title>
                    <v-card-text class="headline font-weight-bold">
                      <!-- $11,156,527,456.00 -->
                      {{ takingsTotalSalesData.NetSales | currency }}
                    </v-card-text>
                  </v-card>
                </skeletons-summary>

                <skeletons-summary :loading="takingsTotalSalesLoading">
                  <v-card class="secondary mb-2" dark>
                    <v-card-title class="py-1 text-subtitle-1">
                      <v-icon left> mdi-cash </v-icon>
                      Cash Sales:
                    </v-card-title>
                    <v-card-text class="headline font-weight-bold">
                      <!-- $11,156,527,456.00 -->
                      {{ takingsTotalSalesData.Cash | currency }}
                    </v-card-text>
                  </v-card>
                </skeletons-summary>

                <skeletons-summary :loading="takingsTotalSalesLoading">
                  <v-card class="secondary mb-2" dark>
                    <v-card-title class="py-1 text-subtitle-1">
                      <v-icon left> mdi-cash </v-icon>
                      Card Sales:
                    </v-card-title>
                    <v-card-text class="headline font-weight-bold">
                      <!-- $11,156,527,456.00 -->
                      {{ takingsTotalSalesData.Card | currency }}
                    </v-card-text>
                  </v-card>
                </skeletons-summary>

                <skeletons-summary :loading="takingsTotalSalesLoading">
                  <v-card class="secondary mb-2" dark>
                    <v-card-title class="py-1 text-subtitle-1">
                      <v-icon left> mdi-cash </v-icon>
                      Voucher Sales:
                    </v-card-title>
                    <v-card-text class="headline font-weight-bold">
                      <!-- $11,156,527,456.00 -->
                      {{ takingsTotalSalesData.Voucher | currency }}
                    </v-card-text>
                  </v-card>
                </skeletons-summary>
              </v-col>

              <v-col lg="6" md="12" sm="6" cols="12">
                <skeletons-graph :loading="takingsTotalSalesLoading">
                  <charts-pie
                    :chartdata="totalSalesDonutChartData"
                    :options="options"
                  />
                </skeletons-graph>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1">
            <v-icon left dense> mdi-chart-pie </v-icon>
            Total Sales By Stall
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col lg="12" md="12" sm="12" cols="12">
                <skeletons-graph>
                  <charts-pie
                    :chartdata="takingsStallComparisonDonutChartData"
                    :options="options"
                  />
                </skeletons-graph>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1">
            <v-icon left dense> mdi-chart-line </v-icon>
            Total Sales Timeline
          </v-card-title>
          <v-card-text>
            <skeletons-graph :loading="takingsTotalSalesLoading">
              <charts-line
                :chartdata="stallComparisonTimelineChartData"
                isCurrency
              />
            </skeletons-graph>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1">
            <v-icon left dense> mdi-file-table </v-icon>
            Stall Takings Table
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              v-model="selectedTransaction"
              :headers="headers"
              :items="stallTableData"
            >
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1">
            <v-icon left dense> mdi-file-table </v-icon>
            Stall Tags Table
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              v-model="selectedTransaction"
              :headers="headersForTagTable"
              :items="stallTagsData"
            >
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import "izitoast/dist/css/iziToast.min.css";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import { buildStallComparisonTimelineChartData } from "@/utils/timelineChart";
import { downloadFromUrl } from "@/utils/download";
import { dashboardAPI } from "@/services";

export default {
  data: () => ({
    takingsReportLoading: false,
    takingsTotalSalesData: {
      NetSales: 0,
      Cash: 0,
      Card: 0,
      Voucher: 0,
    },

    totalSalesDonutChartData: {
      labels: ["No Data Available"],
      datasets: [
        {
          backgroundColor: [
            "rgb(255, 99, 132)",
            "rgb(54, 162, 235)",
            "rgb(255, 205, 86)",
          ],
          data: [1],
          hoverBorderWidth: 3,
        },
      ],
    },
    takingsStallComparisonDonutChartData: {
      labels: ["No Data Available"],
      datasets: [
        {
          backgroundColor: [
            "rgb(255, 99, 132)",
            "rgb(54, 162, 235)",
            "rgb(255, 205, 86)",
          ],
          data: [1],
          hoverBorderWidth: 3,
        },
      ],
    },

    stallComparisonTimelineChartData: {
      labels: ["No Data Available"],
      datasets: [
        {
          label: "No Data Available",
          borderColor: "#FF0E00",
          pointBackgroundColor: "white",
          borderWidth: 1,
          pointBorderColor: "#FF0E00",
          backgroundColor: "transparent",
          data: [0],
        },
      ],
    },

    stallTableData: [],

    stallTagsData: [],

    selectedTransaction: [],
    headers: [
      { text: "Stall Name", value: "Stall Name" },
      {
        text: "Total Takings",
        value: "Sales",
        sort: (a, b) =>
          parseFloat(a.replace(/[^0-9.-]+/g, "")) -
          parseFloat(b.replace(/[^0-9.-]+/g, "")),
      },
      {
        text: "Cash Takings",
        value: "Cash Takings",
        sort: (a, b) =>
          parseFloat(a.replace(/[^0-9.-]+/g, "")) -
          parseFloat(b.replace(/[^0-9.-]+/g, "")),
      },
      {
        text: "Card Takings",
        value: "Card Takings",
        sort: (a, b) =>
          parseFloat(a.replace(/[^0-9.-]+/g, "")) -
          parseFloat(b.replace(/[^0-9.-]+/g, "")),
      },
      {
        text: "Voucher Takings",
        value: "Voucher Takings",
        sort: (a, b) =>
          parseFloat(a.replace(/[^0-9.-]+/g, "")) -
          parseFloat(b.replace(/[^0-9.-]+/g, "")),
      },
      { text: "# of Terminals", value: "Number of Terminals" },
      {
        text: "Avg $ per Terminal",
        value: "Average per Terminal",
        sort: (a, b) =>
          parseFloat(a.replace(/[^0-9.-]+/g, "")) -
          parseFloat(b.replace(/[^0-9.-]+/g, "")),
      },
    ],

    headersForTagTable: [
      { text: "Tag Name", value: "Stall Tag Name" },
      {
        text: "Total Takings",
        value: "Sales",
        sort: (a, b) =>
          parseFloat(a.replace(/[^0-9.-]+/g, "")) -
          parseFloat(b.replace(/[^0-9.-]+/g, "")),
      },
      {
        text: "Cash Takings",
        value: "Cash Takings",
        sort: (a, b) =>
          parseFloat(a.replace(/[^0-9.-]+/g, "")) -
          parseFloat(b.replace(/[^0-9.-]+/g, "")),
      },
      {
        text: "Card Takings",
        value: "Card Takings",
        sort: (a, b) =>
          parseFloat(a.replace(/[^0-9.-]+/g, "")) -
          parseFloat(b.replace(/[^0-9.-]+/g, "")),
      },
      {
        text: "Voucher Takings",
        value: "Voucher Takings",
        sort: (a, b) =>
          parseFloat(a.replace(/[^0-9.-]+/g, "")) -
          parseFloat(b.replace(/[^0-9.-]+/g, "")),
      },
      {
        text: "Per Head",
        value: "Per Head",
        sort: (a, b) =>
          parseFloat(a.replace(/[^0-9.-]+/g, "")) -
          parseFloat(b.replace(/[^0-9.-]+/g, "")),
      },
    ],

    options: {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        labels: {
          fontColor: "white",
        },
      },
      tooltips: {
        callbacks: {
          label(tooltipItem, data) {
            const totalValue = data.datasets[0].data.reduce(
              (acc, value) => acc + parseInt(value),
              0
            );
            const currentValue = parseInt(
              data.datasets[0].data[tooltipItem.index]
            );
            const percentage = ((currentValue / totalValue) * 100).toFixed(2);
            return `${data.labels[tooltipItem.index]}: ${new Intl.NumberFormat(
              "en-US",
              { style: "currency", currency: "USD" }
            ).format(
              data.datasets[0].data[tooltipItem.index]
            )} (${percentage})%`;
          },
        },
      },
    },

    //loading
    stallComparisonLoading: true,
    timelineLoading: true,
    stallTableLoading: true,
  }),

  computed: {
    ...mapState("dashboard", [
      "takingsTotalSales",
      "takingsTotalSalesLoading",
      "takingsJobProgress",
    ]),
    ...mapState("filter", ["globalEventFilter", "searchStartTime", "searchEndTime"]),
  },

  methods: {
    ...mapMutations("dashboard", ["setTakingsTotalSales"]),
    ...mapActions("dashboard", ["prepareReport", "getReports"]),

    generateTakingsReport() {
      if (this.takingsReportLoading) {
        return Promise.resolve();
      }
      this.takingsReportLoading = true;
      return this.prepareReport("takingsReport")
        .then(async (takingsReport) => {
          console.log("takingsReport: ", takingsReport);
          iziToast.info({
            title: "Takings Report",
            message:
              "Generating takings report — this can take a few minutes on large events…",
            position: "topRight",
            timeout: 5000,
          });
          const start = await this.getReports(takingsReport);
          // Backward compatible: sync response still returns URL directly
          if (start?.S3presignedurl) {
            this.presignedurl = start.S3presignedurl;
            downloadFromUrl(this.presignedurl);
            iziToast.success({
              title: "Takings Report",
              message: "Download started",
              position: "topRight",
            });
            return;
          }
          const jobId = start?.jobId;
          if (!jobId) {
            throw new Error("No report job id returned");
          }
          await this.pollTakingsReportJob(jobId);
        })
        .catch((err) => {
          console.error(err);
          iziToast.warning({
            title: "Takings Report",
            message:
              err?.response?.data?.error ||
              err?.message ||
              "Error getting Takings Report, please contact TSG",
            position: "center",
          });
        })
        .finally(() => {
          this.takingsReportLoading = false;
        });
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async pollTakingsReportJob(jobId) {
      const api = dashboardAPI.getReportsAPI();
      const maxAttempts = 150; // ~5 min at 2s
      let lastToastAt = 0;

      for (let i = 0; i < maxAttempts; i++) {
        if (i > 0) {
          await this.sleep(2000);
        }
        const status = await api.getTakingsReportStatus(jobId);
        const state = (status && status.status) || "";
        const phase = status?.phase || "";

        const now = Date.now();
        if (now - lastToastAt >= 12000 && state !== "COMPLETE") {
          lastToastAt = now;
          iziToast.info({
            title: "Takings Report",
            message: phase
              ? `Still generating (${phase})…`
              : "Still generating…",
            position: "topRight",
            timeout: 3500,
          });
        }

        if (state === "COMPLETE") {
          if (!status.S3presignedurl) {
            throw new Error("Report finished but no download URL");
          }
          this.presignedurl = status.S3presignedurl;
          downloadFromUrl(this.presignedurl);
          iziToast.success({
            title: "Takings Report",
            message:
              status.rowCount != null
                ? `Download started (${status.rowCount.toLocaleString()} rows)`
                : "Download started",
            position: "topRight",
          });
          return;
        }
        if (state === "FAILED") {
          throw new Error(status.error || "Takings report failed");
        }
      }
      throw new Error("Takings report timed out — try again or contact TSG");
    },

    generateVoucherReport() {
      this.prepareReport("vouchersReport")
        .then((voucherReport) => {
          iziToast.success({
            title: "Voucher Report",
            message: "Generating voucher report, standby",
            position: "topRight",
          });
          return this.getReports(voucherReport);
        })
        .then((response) => {
          if (response?.S3presignedurl) {
            downloadFromUrl(response.S3presignedurl);
          } else {
            throw new Error("No download URL returned");
          }
        })
        .catch((err) => {
          console.error(err);
          iziToast.warning({
            title: "Voucher Report",
            message: "Error getting Voucher Report, please contact TSG",
            position: "center",
          });
        });
    },

    // ProcessTakingsTotalSalesData
    ProcessTakingsTotalSalesData() {
      const totalSalesData = this.takingsTotalSales?.total_sales_data || {};

      if (totalSalesData.NetSales == null) {
        this.takingsTotalSalesData = {
          NetSales: 0,
          Cash: 0,
          Card: 0,
          Voucher: 0,
        };
        return;
      }
        if (totalSalesData.NetSales) {
          this.takingsTotalSalesData.NetSales = totalSalesData.NetSales;
        } else {
          this.takingsTotalSalesData.NetSales = 0;
        }
        if (totalSalesData.Cash) {
          this.takingsTotalSalesData.Cash = totalSalesData.Cash;
        } else {
          this.takingsTotalSalesData.Cash = 0;
        }
        if (totalSalesData.Card) {
          this.takingsTotalSalesData.Card = totalSalesData.Card;
        } else {
          this.takingsTotalSalesData.Card = 0;
        }
        if (totalSalesData.Voucher) {
          this.takingsTotalSalesData.Voucher = totalSalesData.Voucher;
        } else {
          this.takingsTotalSalesData.Voucher = 0;
        }
    },

    // ProcessTakingsTotalSalesDataPie
    ProcessTakingsTotalSalesDataPie() {
      const totalSalesData = this.takingsTotalSales?.total_sales_data || {};
      if (totalSalesData?.NetSales > 0) {
        this.totalSalesDonutChartData = {
          labels: ["Cash", "Card", "Voucher"],
          datasets: [
            {
              backgroundColor: [
                "rgb(255, 99, 132)",
                "rgb(54, 162, 235)",
                "rgb(255, 205, 86)",
              ],
              data: [
                totalSalesData.Cash,
                totalSalesData.Card,
                totalSalesData.Voucher,
              ],
              hoverBorderWidth: 3,
            },
          ],
        };
      } else {
        this.totalSalesDonutChartData = {
          labels: ["No data available"],
          datasets: [
            {
              backgroundColor: [
                "rgb(255, 99, 132)",
                "rgb(54, 162, 235)",
                "rgb(255, 205, 86)",
              ],
              data: [1],
              hoverBorderWidth: 3,
            },
          ],
        };
      }
    },

    // ProcessTakingsStallComparisonDonutDataPie
    ProcessTakingsStallComparisonDonutDataPie() {
      const comparisonData =
        this.takingsTotalSales?.stall_comparison_data || [];

      if (comparisonData.length > 0) {
        this.takingsStallComparisonDonutChartData = {
          labels: comparisonData.map((x) => x["Stall Name"]),
          datasets: [
            {
              backgroundColor: this.$generateColors.getColors(
                comparisonData.length
              ),
              data: comparisonData.map((x) => x.Sales),
              hoverBorderWidth: 3,
            },
          ],
        };
      } else {
        this.takingsStallComparisonDonutChartData = {
          labels: ["No data available"],
          datasets: [
            {
              backgroundColor: this.$generateColors.getColors(1), // Just get one color for no data
              data: [1],
              hoverBorderWidth: 3,
            },
          ],
        };
      }
    },

    // ProcessStallComparisonTimelineChartData
    ProcessStallComparisonTimelineChartData() {
      const timelineData =
        this.takingsTotalSales?.stall_comparison_timeline || [];

      this.stallComparisonTimelineChartData =
        buildStallComparisonTimelineChartData(timelineData, (index) =>
          this.$generateColors.getColor(index)
        );
    },

    // ProcessStallTableData
    ProcessStallTableData() {
      const stallTable = this.takingsTotalSales?.stall_table_data || [];
      if (stallTable?.length > 0) {
        this.stallTableData = stallTable.map((item) => ({
          "Stall Name": item["Stall Name"],
          Sales:
            item["Sales"] !== undefined
              ? this.$options.filters.currency(item["Sales"])
              : "N/A",
          "Cash Takings":
            item["Cash Takings"] !== undefined
              ? this.$options.filters.currency(item["Cash Takings"])
              : "N/A",
          "Card Takings":
            item["Card Takings"] !== undefined
              ? this.$options.filters.currency(item["Card Takings"])
              : "N/A",
          "Voucher Takings":
            item["Voucher Takings"] !== undefined
              ? this.$options.filters.currency(item["Voucher Takings"])
              : "N/A",
          "Number of Terminals": item["Number of Terminals"],
          "Average per Terminal":
            item["Average per Terminal"] !== undefined
              ? this.$options.filters.currency(item["Average per Terminal"])
              : "N/A",
        }));
      } else {
        this.stallTableData = [];
      }
    },

    ProcessStallTagsTableData() {
      const tagsTable = this.takingsTotalSales?.stall_tags_table_data || [];
      const filteredTagsTable = tagsTable.filter((item) => (item["Sales"] || 0) > 0);
      if (filteredTagsTable.length > 0) {
        this.stallTagsData = filteredTagsTable.map((item) => ({
          "Stall Tag Name": item["Stall Tag Name"],
          Sales:
            item["Sales"] !== undefined
              ? this.$options.filters.currency(item["Sales"])
              : "N/A",
          "Per Head":
            item["Per Head"] !== undefined
              ? this.$options.filters.currency(item["Per Head"])
              : "N/A",
          "Cash Takings":
            item["Cash Takings"] !== undefined
              ? this.$options.filters.currency(item["Cash Takings"])
              : "N/A",
          "Card Takings":
            item["Card Takings"] !== undefined
              ? this.$options.filters.currency(item["Card Takings"])
              : "N/A",
          "Voucher Takings":
            item["Voucher Takings"] !== undefined
              ? this.$options.filters.currency(item["Voucher Takings"])
              : "N/A",
        }));
      } else {
        this.stallTagsData = [];
      }
    },
  },

  watch: {
    "globalEventFilter.EventId"() {
      this.setTakingsTotalSales({});
      this.takingsTotalSalesData = {
        NetSales: 0,
        Cash: 0,
        Card: 0,
        Voucher: 0,
      };
    },
    "takingsTotalSales.total_sales_data": {
      handler() {
        this.ProcessTakingsTotalSalesData();
        this.ProcessTakingsTotalSalesDataPie();
      },
      immediate: true,
    },

    "takingsTotalSales.stall_comparison_data": {
      handler() {
        this.ProcessTakingsStallComparisonDonutDataPie();
      },
      immediate: true,
    },

    "takingsTotalSales.stall_comparison_timeline": {
      handler() {
        this.ProcessStallComparisonTimelineChartData();
      },
      immediate: true,
    },

    "takingsTotalSales.stall_table_data": {
      handler() {
        this.ProcessStallTableData();
      },
      immediate: true,
    },

    "takingsTotalSales.stall_tags_table_data": {
      handler() {
        this.ProcessStallTagsTableData();
      },
      immediate: true,
    },
  },

  created() {
    // this.updateDashboard()
    var self = this;
    setTimeout(function () {
      self.stallComparisonLoading = false;
      self.timelineLoading = false;
      self.stallTableLoading = false;
    }, 2000);
  },
  filters: {
    currency(value) {
      var formatter = new Intl.NumberFormat("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return "$" + formatter.format(value);
    },
  },
};
</script>

<style scoped>
h1 {
  color: #a09c94;
  font-family: sans-serif;
}
</style>
